import React, { useState, useEffect, useCallback } from 'react';
import { 
  getAllLockerStatus, 
  updateLockerStatus, 
  assignLocker,
  assignLockerForParcel,
  subscribeToLockerUpdates,
  markLockerEmpty
} from '../data/firebaseService';
import LockerStatus from './LockerStatus';
import Header from './Header';
import Footer from './Footer';
import './AdminPanel.css';

const AdminPanel = ({ onBack }) => {
  const [selectedLocation, setSelectedLocation] = useState('rajinder_nagar');
  const [selectedLocker, setSelectedLocker] = useState('');
  const [action, setAction] = useState('view');
  const [formData, setFormData] = useState({
    phoneNumber: ''
  });
  const [pinData, setPinData] = useState({
    locker1: '',
    locker2: ''
  });
  const [fullLockerData, setFullLockerData] = useState(null);
  const [allFullLockers, setAllFullLockers] = useState([]);

  const locations = [
    { id: 'rajinder_nagar', name: 'Rajinder Nagar' },
    { id: 'patel_nagar', name: 'Patel Nagar' }
  ];

  const loadCurrentPins = useCallback(async () => {
    try {
      const status = await getAllLockerStatus();
      const locationData = status[selectedLocation] || {};
      console.log('Loading PINs for location:', selectedLocation, locationData);
      const pin1 = locationData.locker1?.pin || '';
      const pin2 = locationData.locker2?.pin || '';
      console.log('Setting PIN data:', { locker1: pin1, locker2: pin2 });
      setPinData({
        locker1: pin1,
        locker2: pin2
      });
    } catch (error) {
      console.error('Error loading current PINs:', error);
    }
  }, [selectedLocation]);

  const loadFullLockers = useCallback(async () => {
    try {
      const status = await getAllLockerStatus();
      const locationData = status[selectedLocation] || {};
      const fullLockers = [];
      
      Object.keys(locationData).forEach(lockerId => {
        const locker = locationData[lockerId];
        // Only show customer drop-off lockers (not staff parcel lockers)
        if (locker.status === 'full' && locker.customerData && locker.customerData.type === 'customer_dropoff') {
          fullLockers.push({
            lockerId,
            ...locker
          });
        }
      });
      
      setAllFullLockers(fullLockers);
    } catch (error) {
      console.error('Error loading full lockers:', error);
    }
  }, [selectedLocation]);

  useEffect(() => {
    // Set up real-time listener for locker updates
    const unsubscribe = subscribeToLockerUpdates((lockers) => {
      // Real-time updates handled by LockerStatus component
    });
    
    return () => unsubscribe();
  }, [selectedLocation]);

  useEffect(() => {
    if (action === 'changePin') {
      loadCurrentPins();
    }
    if (action === 'lockerFull') {
      loadFullLockers();
    }
  }, [action, selectedLocation, loadCurrentPins, loadFullLockers]);

  const handleLockerClick = (lockerId, lockerInfo) => {
    setSelectedLocker(lockerId);
    if (lockerInfo.status === 'empty') {
      setAction('assign');
      setFormData({
        phoneNumber: ''
      });
    } else if (lockerInfo.status === 'full') {
      setAction('lockerFull');
      setFullLockerData(lockerInfo);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Assign locker for staff parcel (staff puts parcel in locker)
      const result = await assignLockerForParcel(selectedLocation, selectedLocker, formData.phoneNumber);
      
      if (result) {
        console.log('Locker assigned for parcel successfully:', result);
        setAction('view');
        setSelectedLocker('');
        setFormData({ phoneNumber: '' });
        alert('Parcel assigned to locker successfully! Customer can pick up using their phone number.');
      } else {
        console.error('Failed to assign locker for parcel');
        alert('Failed to assign locker for parcel. Please try again.');
      }
    } catch (error) {
      console.error('Error assigning locker for parcel:', error);
      alert('Error assigning locker for parcel. Please try again.');
    }
  };





  const handleSavePins = async () => {
    try {
      console.log('Saving PINs for location:', selectedLocation, 'PINs:', pinData);
      
      // Update PINs for both lockers in the selected location
      const promises = [];
      
      if (pinData.locker1) {
        promises.push(updateLockerStatus(selectedLocation, 'locker1', 'empty', pinData.locker1));
      }
      if (pinData.locker2) {
        promises.push(updateLockerStatus(selectedLocation, 'locker2', 'empty', pinData.locker2));
      }
      
      await Promise.all(promises);
      setAction('view');
    } catch (error) {
      console.error('Error saving PINs:', error);
      alert('Error saving PINs. Please try again.');
    }
  };

  const handleMarkLockerVacant = async () => {
    if (!selectedLocker) return;
    
    try {
      const success = await markLockerEmpty(selectedLocation, selectedLocker);
      
      if (success) {
        console.log('Locker marked as vacant successfully');
        setAction('view');
        setSelectedLocker('');
        setFullLockerData(null);
        alert('Locker has been marked as vacant successfully!');
      } else {
        console.error('Failed to mark locker as vacant');
        alert('Failed to mark locker as vacant. Please try again.');
      }
    } catch (error) {
      console.error('Error marking locker vacant:', error);
      alert('Error marking locker vacant. Please try again.');
    }
  };

  return (
    <div className="admin-panel">
      <Header title="Staff Admin Panel" />
      <div className="admin-container">
        <div className="admin-header">
          <button className="back-btn" onClick={onBack}>
            ← Back
          </button>
          <h1>👨‍💼 Staff Admin Panel</h1>
        </div>

        <div className="admin-controls">
          <div className="location-selector">
            <label>📍 Select Location:</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

                     <div className="action-buttons">
             <button
               className={`action-btn ${action === 'view' ? 'active' : ''}`}
               onClick={() => setAction('view')}
             >
               👁️ View Status
             </button>
             <button
               className={`action-btn ${action === 'changePin' ? 'active' : ''}`}
               onClick={() => setAction('changePin')}
             >
               🔐 Change PIN
             </button>
             <button
               className={`action-btn ${action === 'lockerFull' ? 'active' : ''}`}
               onClick={() => setAction('lockerFull')}
             >
               📦 Locker Full
             </button>
           </div>
        </div>

        <div className="admin-content">
          {action === 'view' && (
            <LockerStatus
              location={selectedLocation}
              onLockerClick={handleLockerClick}
            />
          )}

                     {action === 'assign' && (
             <div className="admin-form-section">
               <h3>
                 📦 Assign Parcel to Locker
                 {selectedLocker && ` - ${selectedLocker.toUpperCase()}`}
               </h3>
               <p className="parcel-info">
                 Assign locker for staff parcel. Customer will pick up using their phone number.
               </p>

               <form onSubmit={handleFormSubmit} className="admin-form">
                 <div className="form-row">
                   <div className="form-group">
                     <label>📱 Customer Phone Number</label>
                     <input
                       type="tel"
                       value={formData.phoneNumber}
                       onChange={(e) => setFormData(prev => ({...prev, phoneNumber: e.target.value}))}
                       placeholder="Enter customer phone number"
                       required
                     />
                   </div>
                 </div>

                 <div className="form-actions">
                   <button type="submit" className="submit-btn">
                     📦 Assign Parcel
                   </button>
                   
                   <button
                     type="button"
                     className="cancel-btn"
                     onClick={() => {
                       setAction('view');
                       setSelectedLocker('');
                     }}
                   >
                     ❌ Cancel
                   </button>
                 </div>
               </form>
             </div>
           )}

          {action === 'changePin' && (
            <div className="admin-form-section">
              {console.log('Rendering Change PIN section, pinData:', pinData)}
              <h3>
                🔐 Set Custom PIN - {locations.find(loc => loc.id === selectedLocation)?.name}
              </h3>
              <p className="pin-info">
                Set custom PINs for lockers. These PINs will remain fixed and won't change automatically.
              </p>
              
              <div className="pin-management-form">
                <div className="pin-row">
                  <div className="pin-group">
                    <label>🔒 Locker 1 PIN</label>
                    <input
                      type="text"
                      value={pinData.locker1}
                      onChange={(e) => setPinData(prev => ({...prev, locker1: e.target.value}))}
                      maxLength="4"
                      placeholder="Enter PIN"
                    />
                  </div>
                  
                  <div className="pin-group">
                    <label>🔒 Locker 2 PIN</label>
                    <input
                      type="text"
                      value={pinData.locker2}
                      onChange={(e) => setPinData(prev => ({...prev, locker2: e.target.value}))}
                      maxLength="4"
                      placeholder="Enter PIN"
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleSavePins}
                  >
                    💾 Save PINs
                  </button>
                  
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setAction('view')}
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

                     {action === 'lockerFull' && (
             <div className="admin-form-section">
               <h3>
                 👕 Customer Drop-Off Lockers - {locations.find(loc => loc.id === selectedLocation)?.name}
               </h3>
               <p className="dropoff-info">
                 Shows only lockers where customers dropped off clothes (not staff parcel lockers).
               </p>
               
               {allFullLockers.length > 0 ? (
                 <div className="full-lockers-list">
                   <h4>📋 Customer Drop-Off Forms</h4>
                   {allFullLockers.map((locker, index) => (
                     <div key={index} className="locker-customer-card">
                       <div className="locker-header">
                         <h5>🔒 Locker {locker.lockerId.toUpperCase()}</h5>
                         <span className="pin-display">PIN: {locker.pin}</span>
                       </div>
                       
                       <div className="customer-form-display">
                         <div className="form-details">
                           <div className="detail-row">
                             <label>📱 Phone Number:</label>
                             <span>{locker.customerData.phoneNumber}</span>
                           </div>
                           <div className="detail-row">
                             <label>🛠️ Service Type:</label>
                             <span>{locker.customerData.serviceType}</span>
                           </div>
                           <div className="detail-row">
                             <label>👕 Number of Clothes:</label>
                             <span>{locker.customerData.numberOfClothes} items</span>
                           </div>
                           <div className="detail-row">
                             <label>📅 Drop-Off Time:</label>
                             <span>{new Date(locker.customerData.dropOffTime).toLocaleString()}</span>
                           </div>
                         </div>
                         
                         <div className="form-actions">
                           <button
                             type="button"
                             className="submit-btn"
                             onClick={() => {
                               setSelectedLocker(locker.lockerId);
                               setFullLockerData(locker);
                               handleMarkLockerVacant();
                             }}
                           >
                             ✅ Mark Locker Vacant
                           </button>
                         </div>
                       </div>
                     </div>
                   ))}
                   
                   <div className="form-actions">
                     <button
                       type="button"
                       className="cancel-btn"
                       onClick={() => {
                         setAction('view');
                         setSelectedLocker('');
                         setFullLockerData(null);
                         setAllFullLockers([]);
                       }}
                     >
                       ← Back to View
                     </button>
                   </div>
                 </div>
               ) : (
                 <div className="no-full-lockers">
                   <p>No customer drop-off lockers found at this location.</p>
                   <button
                     type="button"
                     className="cancel-btn"
                     onClick={() => setAction('view')}
                   >
                     ← Back to View
                   </button>
                 </div>
               )}
             </div>
           )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminPanel; 